import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
export function StorySection() {
  return <section className="py-20 px-4 bg-[#F2F1EE]" data-id="element-114">
      <div className="max-w-3xl mx-auto" data-id="element-115">
        {/* Narrative Hook */}
        <div className="space-y-8 mb-20" data-id="element-116">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-xl md:text-2xl leading-relaxed text-charcoal font-serif" data-id="element-117">
            <p className="text-[#050505]" data-id="element-118">Llegas a casa.</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-xl md:text-2xl leading-relaxed text-charcoal font-serif" data-id="element-119">
            <p className="text-[#000000]" data-id="element-120">
              Los niños piden algo. El teléfono sigue sonando.
            </p>
            <p className="mt-4 text-[#000000]" data-id="element-121">
              Y tú… tú solo quieres apagar el cerebro 10 minutos.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl leading-relaxed text-charcoal-light" data-id="element-122">
            <p className="text-[#000000]" data-id="element-123">Entonces pasa lo de siempre:</p>
            <p className="mt-2 font-medium text-[#DF1189]" data-id="element-124">
              Abres la alacena, "picas" un poquito, luego otro poquito… y
              terminas comiendo lo primero que te calme.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="p-6 bg-white rounded-2xl border-l-4 border-terracotta shadow-sm" data-id="element-125">
            <p className="text-lg text-charcoal italic" data-id="element-126">
              "No es que no sepas qué es saludable. Es que a esta hora, nadie
              tiene energía para pensar."
            </p>
          </motion.div>

          <p className="text-lg text-[#000000]" data-id="element-127">
            Y si tu plan depende de que tú pienses, midas, cocines y limpies
            todos los días… no es un plan.{' '}
            <span className="font-bold text-[#DF1189]" data-id="element-128">Es una trampa.</span>
          </p>
        </div>

        {/* Divider */}
        <div className="flex justify-center mb-20" data-id="element-129">
          <div className="w-16 h-1 rounded-full bg-[#DF1189]" data-id="element-130"></div>
        </div>

        {/* Audience Identification */}
        <div className="mb-20" data-id="element-131">
          <h3 className="text-3xl font-serif font-bold mb-8 text-center text-[#DF1189]" data-id="element-132">
            Esto es para la mujer que…
          </h3>
          <ul className="space-y-4 bg-white p-8 rounded-2xl shadow-sm border border-cream" data-id="element-133">
            {['Trabaja y aun así carga la casa en la cabeza.', "Quiere cuidarse, pero está cansada de 'empezar el lunes'.", 'No quiere otra dieta, quiere una forma real de comer.', 'Ama a sus hijos, pero ya se cansó de ser la última en su lista.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-134">
                <div className="mt-1 min-w-[20px]" data-id="element-135">
                  <Check className="w-5 h-5 text-olive" data-id="element-136" />
                </div>
                <span className="text-lg text-charcoal-light" data-id="element-137">{item}</span>
              </li>)}
          </ul>
          <p className="mt-8 text-center text-lg text-charcoal font-medium" data-id="element-138">
            Si estás en tus 35–50, con hijos, y tu día se siente como correr
            todo el tiempo… <br data-id="element-139" />
            <span className="font-bold font-serif text-2xl block mt-2 text-[#DF1189]" data-id="element-140">
              tu problema se llama: fricción.
            </span>
          </p>
        </div>

        {/* The Struggle */}
        <div className="grid md:grid-cols-2 gap-12 mb-20" data-id="element-141">
          <div data-id="element-142">
            <h3 className="text-2xl font-serif font-bold text-charcoal mb-6" data-id="element-143">
              La fricción se ve así
            </h3>
            <ul className="space-y-3" data-id="element-144">
              {['¿Qué hago de comer? (otra decisión más).', 'No tengo nada descongelado', 'No quiero ensuciar', 'Ya es tarde', 'Mejor algo rápido… total mañana me pongo seria'].map((item, i) => <li key={i} className="flex items-center gap-3 text-charcoal-light" data-id="element-145">
                  <X className="w-5 h-5 text-red-400/70" data-id="element-146" />
                  <span data-id="element-147">{item}</span>
                </li>)}
            </ul>
            <p className="mt-6 text-charcoal italic" data-id="element-148">
              Y sin darte cuenta, vuelves al patrón: <br data-id="element-149" />
              <span className="font-bold" data-id="element-150">
                improvisar → picar → culpa → reiniciar.
              </span>
            </p>
          </div>

          <div data-id="element-151">
            <h3 className="text-2xl font-serif font-bold text-charcoal mb-6" data-id="element-152">
              Lo que probaste (y falló)
            </h3>
            <div className="space-y-6" data-id="element-153">
              <div data-id="element-154">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-155">
                  1. Dietas estrictas
                </h4>
                <p className="text-sm text-charcoal-light" data-id="element-156">
                  Funcionan… hasta el primer día real.
                </p>
              </div>
              <div data-id="element-157">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-158">
                  2. Meal prep "perfecto"
                </h4>
                <p className="text-sm text-charcoal-light" data-id="element-159">
                  Tres horas el domingo, 18 ingredientes… y el miércoles ya lo
                  odias.
                </p>
              </div>
              <div data-id="element-160">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-161">
                  3. Recetas sueltas
                </h4>
                <p className="text-sm text-charcoal-light" data-id="element-162">
                  Ricas, sí. Pero igual te dejan con el mismo problema: decidir
                  cada día.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center p-8 rounded-2xl bg-[#DF1189]" data-id="element-163">
          <p className="text-xl md:text-2xl font-serif font-bold text-[#F2F1EE]" data-id="element-164">
            Tú no necesitas más recetas. <br data-id="element-165" />
            Necesitas un sistema que sobreviva tu vida.
          </p>
        </div>
      </div>
    </section>;
}